/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors in child component tree and displays a fallback UI.
 * Prevents the entire application from crashing due to component errors.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

import { Component, type ReactNode, type JSX } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

export interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Custom fallback UI to render when an error is caught */
  fallback?: ReactNode;
  /** Callback called when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Whether to show error details (useful for development) */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * ErrorBoundary catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the crashed component tree.
 *
 * Features:
 * - Catches rendering errors in child components
 * - Custom fallback UI support
 * - Error callback for logging/reporting
 * - Reset functionality to retry rendering
 * - Optional error details display
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   fallback={<div>Something went wrong</div>}
 *   onError={(error) => console.error(error)}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });

    // Call the error callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Render default error UI
      return (
        <div
          className="p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
          data-testid="error-boundary-fallback"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">
                Something went wrong
              </h3>
              <p className="mt-1 text-sm text-red-700">
                An error occurred while rendering this component.
                Please try refreshing the page or contact support if the problem persists.
              </p>

              {/* Show error details in development */}
              {this.props.showDetails && this.state.error && (
                <details className="mt-3">
                  <summary className="text-sm text-red-600 cursor-pointer hover:text-red-800">
                    Error details
                  </summary>
                  <pre className="mt-2 p-2 bg-red-100 rounded text-xs text-red-800 overflow-auto max-h-40">
                    {this.state.error.message}
                    {'\n\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}

              <button
                type="button"
                onClick={this.handleReset}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                data-testid="error-boundary-reset"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Default fallback components for common use cases
 */
export function FilterErrorFallback(): JSX.Element {
  return (
    <div
      className="p-4 bg-yellow-50 border-b border-yellow-200"
      role="alert"
      data-testid="filter-error-fallback"
    >
      <p className="text-sm text-yellow-800">
        Unable to load filters. Please refresh the page.
      </p>
    </div>
  );
}

export function TableErrorFallback(): JSX.Element {
  return (
    <div
      className="p-8 bg-gray-50 rounded-lg text-center"
      role="alert"
      data-testid="table-error-fallback"
    >
      <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-3" aria-hidden="true" />
      <p className="text-gray-600">
        Unable to load transactions. Please refresh the page.
      </p>
    </div>
  );
}

export default ErrorBoundary;
